/**
 * One person on the sourcing shortlist, with the recruiter's decision buttons (Sofia, Oct 19 2026).
 * Moved out of SourcingPanel (Oct 23 2026) when the "not read yet" list came in.
 *
 * `unread` (Sofia, Oct 23 2026): the full profile has not been read and checked yet. Accept waits
 * for that — the recruiter decides on the whole CV, not on the three positions of a list card (on
 * Oct 22 every rejection on a Fleet shortlist was of someone whose profile had not been read).
 * Reject stays: a card can already show enough to say no.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import type { OutreachCandidate, SourcingVerdict } from '../../types/taOutreach';
import { Avatar, Card, EvidenceChip, OwnerName } from './outreachUi';
import { profileLinkOf } from './profileLink';

const VERDICT: Record<SourcingVerdict, { tone: 'green' | 'red' | 'amber'; prefix: string }> = {
  yes: { tone: 'green', prefix: '' },
  no: { tone: 'red', prefix: 'Not shown: ' },
  unknown: { tone: 'amber', prefix: 'Not known: ' },
};
const TONE = { green: 'bg-[#DEF5E6] text-[#1D9247]', red: 'bg-[#FCDFE4] text-[#E71D3F]', amber: 'bg-[#FCEEE2] text-[#B96716]' };

/** What a shortlist decision does, on hover (Sofia, Oct 19 2026). */
function Hint({ text, children }: { text: string; children: React.ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs text-xs text-white">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

export function ShortlistCard({
  c,
  onDecide,
  busy,
  selected,
  onSelect,
  unread = false,
  onRead,
}: {
  c: OutreachCandidate;
  onDecide: (d: 'accept' | 'reject', reason?: string) => void;
  busy: boolean;
  /** Bulk accept: undefined = no checkbox (the "not a fit" list). */
  selected?: boolean;
  onSelect?: (on: boolean) => void;
  /** Not read and checked yet: Accept waits for the full profile. */
  unread?: boolean;
  /** Read this one profile now (the FINN Outreach Sender); undefined = not possible right now. */
  onRead?: () => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const s = c.sourcing;
  return (
    <Card className="flex gap-3.5">
      {onSelect && <input type="checkbox" aria-label={`Select ${c.name}`} checked={Boolean(selected)} onChange={(e) => onSelect(e.target.checked)} className="mt-3" />}
      <Avatar name={c.name} />
      <div className="min-w-0 flex-1 space-y-2 text-[13px]">
        <div>
          {profileLinkOf(c) ? (
            <a href={profileLinkOf(c) as string} target="_blank" rel="noreferrer" className="font-semibold text-[#1A1A1A] underline" title="Open the LinkedIn profile">
              {c.name}
            </a>
          ) : (
            <span className="font-semibold">{c.name}</span>
          )}{' '}
          <OwnerName owner={c.owner} />
          <span className="block max-w-[560px] leading-snug text-[#707070]">{[c.headline, c.location, s?.pipeline && ['saved', 'already'].includes(s.pipeline.status) ? 'In the LinkedIn project pipeline' : null].filter(Boolean).join(' · ')}</span>
        </div>
        {s?.skipReason && <p className="m-0 text-[#B96716]">The agent thinks this is not a fit: {s.skipReason}</p>}
        {c.gap ? (
          <div className="space-y-1">
            <span className="block text-xs text-[#707070]">Full profile checked against the brief</span>
            <div className="flex flex-wrap gap-1.5">
              {c.gap.criteria.filter((x) => x.mustHave || x.verdict === 'evidenced').map((x) => (
                <EvidenceChip key={x.id} c={x} />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <span className="block text-xs text-[#707070]">{c.profileText ? 'Full profile read · check pending' : 'From the Recruiter list (3 positions)'}</span>
            <div className="flex flex-wrap gap-1.5">
              {(s?.evidence || []).map((e) => (
                <span key={e.label} className={`inline-block rounded px-2 py-1 text-xs ${TONE[VERDICT[e.verdict].tone]}`} title={e.note || undefined}>
                  {VERDICT[e.verdict].prefix}
                  {e.label}
                </span>
              ))}
            </div>
          </div>
        )}
        {s?.notes && <p className="m-0 text-xs text-[#707070]">{s.notes}</p>}
        {rejecting && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[260px] flex-1">
              <Input label="Why not? (kept with the decision and in the quality check)" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
            </div>
            <Button variant="primary" size="sm" className="rounded-md" disabled={busy} onClick={() => onDecide('reject', reason)}>
              Reject
            </Button>
            <Button variant="ghost" size="sm" className="rounded-md" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>
      {!rejecting && (
        <div className="flex shrink-0 flex-col items-stretch gap-1.5">
          {unread ? (
            <Hint text="Read the full profile first: you decide on the whole CV, not on the list card.">
              <Button variant="primary" size="sm" className="w-24 justify-center rounded-md" disabled={busy || !onRead} onClick={onRead}>
                {busy ? 'Working…' : 'Read profile'}
              </Button>
            </Hint>
          ) : (
            <Hint text="It’s moved to the next stage, where you can send the message.">
              <Button variant="primary" size="sm" className="w-24 justify-center rounded-md" disabled={busy} onClick={() => onDecide('accept')}>
                {busy ? 'Working…' : 'Accept'}
              </Button>
            </Hint>
          )}
          <Hint text="Takes them off the shortlist. No later sourcing run brings them back for this role.">
            <Button variant="ghost" size="sm" className="w-24 justify-center rounded-md text-[#707070]" disabled={busy} onClick={() => setRejecting(true)}>
              Reject
            </Button>
          </Hint>
        </div>
      )}
    </Card>
  );
}
